import { planLabel, type BillingConfig } from '@nolens/domain';
import { cx, Logo, Switch, useLocale } from '@nolens/ui';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api } from '../lib/api';
import { CONTACT_EMAIL } from '../lib/contact';
import { openCheckout } from '../lib/paddle';
import { pricingText, type PricingText } from '../i18n/pricing';

/** Ordem dos cartões: o Equipa ao meio, em destaque (no telemóvel, em primeiro). */
const PLANS = ['free', 'team', 'pro'] as const;
type Plan = (typeof PLANS)[number];

function Check() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 16 16"
      className="mt-[3px] size-4 shrink-0 text-accent-text"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 8.5 6.5 12 13 4.5" />
    </svg>
  );
}

function Features({ items }: { items: string[] }) {
  return (
    <ul className="mt-3 flex flex-col gap-2.5 text-[14.5px]">
      {items.map((f) => (
        <li key={f} className="flex gap-2.5">
          <Check />
          <span>{f}</span>
        </li>
      ))}
    </ul>
  );
}

/** O botão de cada plano: Grátis entra; Equipa cria a equipa e subscreve; Pro subscreve. */
function Cta({ id, t, featured }: { id: Plan; t: PricingText; featured: boolean }) {
  const cls = cx(
    'mt-5 inline-flex h-11 items-center justify-center rounded-full text-[15px] font-semibold transition',
    featured
      ? 'bg-accent text-on-accent hover:brightness-95'
      : 'shadow-[inset_0_0_0_1px_var(--border-strong)] hover:bg-surface-2',
  );
  const label = t.plans[id].cta;
  if (id === 'free')
    return (
      <Link to="/entrar" className={cls}>
        {label}
      </Link>
    );
  if (id === 'team')
    return (
      <Link to="/equipas/nova" className={cls}>
        {label}
      </Link>
    );
  // Pro: entra (se for preciso) e abre o plano do espaço pessoal, onde se subscreve
  return (
    <Link to="/subscrever" className={cls}>
      {label}
    </Link>
  );
}

/** Planos e preços (pública). Faturação anual por omissão; pagamentos pelo Paddle. */
export function PricingPage() {
  const locale = useLocale();
  const t = pricingText(locale);
  const [yearly, setYearly] = useState(true);
  const [params] = useSearchParams();
  const ptxn = params.get('_ptxn');

  // Link de pagamento do Paddle (ex.: o email para atualizar o cartão): abre o checkout dessa
  // transação por cima desta página.
  useEffect(() => {
    if (!ptxn || !/^txn_[a-z0-9]+$/.test(ptxn)) return;
    void api<BillingConfig | null>('/public/billing', { anonymous: true })
      .then((cfg) =>
        cfg
          ? openCheckout({ ...cfg, transactionId: ptxn, email: null }, locale, {
              completed: () => undefined,
              closed: () => undefined,
            })
          : undefined,
      )
      .catch(() => undefined);
  }, [ptxn, locale]);
  const cell = (v: string | boolean) =>
    v === true ? (
      <span aria-label={t.yes} className="text-positive">
        ✓
      </span>
    ) : v === false ? (
      <span aria-label={t.no} className="text-text-3">
        —
      </span>
    ) : (
      v
    );
  return (
    <main className="mx-auto max-w-6xl px-5 py-10">
      <Link to="/" aria-label={t.home}>
        <Logo />
      </Link>
      <div className="mt-8 flex flex-wrap items-end justify-between gap-6">
        <div className="max-w-2xl">
          <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">{t.title}</h1>
          <p className="mt-3 text-text-2">{t.intro}</p>
        </div>
        <div className="w-full max-w-[300px]" data-billing={yearly ? 'yearly' : 'monthly'}>
          <Switch
            checked={yearly}
            onChange={setYearly}
            label={t.annual}
            description={t.annualSave}
          />
        </div>
      </div>

      <div className="mt-10 grid items-start gap-4 md:mt-14 md:grid-cols-3 md:gap-0" data-plans>
        {PLANS.map((id) => {
          const p = t.plans[id];
          const featured = id === 'team';
          const [unitA, unitB] = t.unit(id);
          return (
            <section
              key={id}
              data-plan-card={id}
              className={cx(
                'flex flex-col rounded-[20px] p-6',
                featured
                  ? 'order-first z-10 bg-surface-0 shadow-[inset_0_0_0_2px_var(--accent),0_18px_50px_-24px_rgb(0_0_0/0.35)] md:order-none md:-mt-10 md:pb-9'
                  : 'bg-surface-1 shadow-[inset_0_0_0_1px_var(--border)]',
                id === 'free' && 'md:rounded-r-none',
                id === 'pro' && 'md:rounded-l-none',
              )}
            >
              <div className="flex items-start justify-between gap-3">
                <h2 className="text-xl font-semibold tracking-tight">{p.name}</h2>
                {featured && (
                  <span className="rounded-full bg-accent-soft px-2.5 py-1 text-[12px] font-semibold text-accent-text">
                    {t.badge}
                  </span>
                )}
              </div>
              <p className="mt-2 min-h-[44px] text-[14.5px] text-text-2">{p.tagline}</p>
              <div className="mt-5 flex items-end gap-2">
                <span className="text-[34px] leading-none font-semibold tracking-tight tabular-nums">
                  {t.price(id, yearly)}
                </span>
                {unitB &&
                  (unitA ? (
                    <span className="flex flex-col pb-0.5 text-[12px] leading-tight text-text-3">
                      <span>/ {unitA}</span>
                      <span>{unitB}</span>
                    </span>
                  ) : (
                    <span className="pb-1 text-[13px] text-text-3">/ {unitB}</span>
                  ))}
              </div>
              <div className="mt-3 min-h-[40px] text-[13px] text-text-3">
                {t.priceNotes(id, yearly).map((n) => (
                  <p key={n}>{n}</p>
                ))}
              </div>
              <Cta id={id} t={t} featured={featured} />

              {featured && (
                <div className="mt-6 rounded-[14px] bg-accent-soft/60 p-4 shadow-[inset_0_0_0_1px_var(--border)]">
                  <p className="font-semibold">{t.teamHighlight.title}</p>
                  <p className="mt-1 text-[13px] text-text-2">{t.teamHighlight.text}</p>
                  <Features items={t.teamHighlight.items} />
                </div>
              )}
              <p className="mt-6 text-[14px] font-semibold">{p.includes}</p>
              <Features items={p.features} />
            </section>
          );
        })}
      </div>
      <p className="mt-4 text-[13px] text-text-3">{t.payments}</p>
      <p className="mt-1 text-[13px] text-text-3" data-enterprise>
        {t.enterprise}{' '}
        <a className="underline underline-offset-2" href={`mailto:${CONTACT_EMAIL}`}>
          {CONTACT_EMAIL}
        </a>
      </p>

      <h2 className="mt-14 text-lg font-semibold">{t.compare}</h2>
      <div className="mt-3 overflow-x-auto rounded-[14px] bg-surface-1 shadow-[inset_0_0_0_1px_var(--border)]">
        <table className="w-full text-[14px]">
          <thead className="text-left text-[12.5px] text-text-3">
            <tr>
              <th className="px-4 py-2.5 font-medium" />
              {(['free', 'pro', 'team'] as const).map((id) => (
                <th key={id} className="px-4 py-2.5 font-medium">
                  {planLabel(id, locale)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {t.rows.map((r) => (
              <tr key={r.label} className="border-t border-border">
                <td className="px-4 py-2.5">{r.label}</td>
                <td className="px-4 py-2.5">{cell(r.free)}</td>
                <td className="px-4 py-2.5">{cell(r.pro)}</td>
                <td className="px-4 py-2.5">{cell(r.team)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-6 text-[13px] text-text-3">{t.trialEnd}</p>
    </main>
  );
}
